import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import Button from '../../ui/Button';

interface WizardErrorSummaryProps {
  errors: string[];
  onDismiss: () => void;
  onJumpToPosition?: (positionIndex: number) => void;
}

const POSITION_PREFIX = /^Position\s+(\d+):/i;

const parsePositionIndex = (error: string): number | null => {
  const match = error.match(POSITION_PREFIX);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const WizardErrorSummary: React.FC<WizardErrorSummaryProps> = ({
  errors,
  onDismiss,
  onJumpToPosition,
}) => {
  if (errors.length === 0) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="mx-4 mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
    >
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-red-200/70 dark:border-red-800/70">
        <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <h4 className="text-sm font-semibold">
            Please fix {errors.length === 1 ? 'this issue' : `these ${errors.length} issues`} before continuing
          </h4>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss errors"
          className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="px-4 py-2 divide-y divide-red-200/60 dark:divide-red-800/60">
        {errors.map((error, idx) => {
          const positionIndex = parsePositionIndex(error);
          return (
            <li
              key={`${idx}-${error}`}
              className="flex items-center justify-between gap-3 py-2 text-sm text-red-800 dark:text-red-200"
            >
              <span className="flex-1">{error}</span>
              {positionIndex !== null && onJumpToPosition && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onJumpToPosition(positionIndex)}
                  className="h-7 px-2 text-xs font-medium text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 hover:bg-red-100 dark:hover:bg-red-900/30 shrink-0"
                >
                  Jump to Position {positionIndex}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WizardErrorSummary;
